import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ApiService, HttpError} from '../../shared/api.service';
import {AuthService} from "../../shared/auth.service";
import {ModalComponent} from '../../shared/modal/modal.component';
import {NavigationService} from '../../shared/navigation.service';

@Component({
  selector: 'credentials-form',
  templateUrl: './credentials-form.component.html',
  styleUrls: ['../styles/account-form.scss', './credentials-form.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CredentialsFormComponent implements OnInit {

  @ViewChild('successModal') successModal: ModalComponent;

  credentialInformation: FormGroup;
  isOldPasswordIncorrect = false;
  isLoading = false;
  masquerading$ = this.Auth.masquerading$;

  constructor(private formBuilder: FormBuilder,
              private Api: ApiService,
              private Navigation: NavigationService,
              private Auth: AuthService) {
  }

  get emailPart() {
    return this.credentialInformation.get('email');
  }

  get passwordPart() {
    return this.credentialInformation.get('password');
  }

  get oldPassword() {
    return this.credentialInformation.get('password.oldPassword');
  }

  get newPassword() {
    return this.credentialInformation.get('password.newPassword');
  }

  get confirmedPassword() {
    return this.credentialInformation.get('password.confirmedPassword');
  }

  isEmailInvalid() {
    return this.emailPart.invalid;
  }

  isOldPasswordMissing() {
    return this.passwordPart.errors && this.passwordPart.errors.oldMissing;
  }

  isNewPasswordMissing() {
    return this.passwordPart.errors
      && this.passwordPart.errors.newMissing
      && this.oldPassword.touched
      && this.newPassword.touched;
  }

  isNewPasswordInvalid() {
    return this.passwordPart.errors && this.passwordPart.errors.newInvalid && this.newPassword.touched;
  }

  isConfirmPasswordInvalid() {
    return this.passwordPart.errors && this.passwordPart.errors.badConfirmation && this.confirmedPassword.touched;
  }

  ngOnInit() {
    this.initializeForm();
    this.loadData();
  }

  initializeForm() {
    this.credentialInformation = this.formBuilder.group({
      email: this.initializeEmail(),
      password: this.initializePassword()
    });
  }

  initializeEmail() {
    return this.formBuilder.group({
      email: ['', Validators.required]
    });
  }

  initializePassword() {
    return this.formBuilder.group({
      oldPassword: [''],
      newPassword: [''],
      confirmedPassword: ['']
    });
  }

  loadData() {
    this.isLoading = true;

    this.Api.getUserCredentialInfo().subscribe(credentials => {
      this.updateForm(credentials);
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  reloadData() {
    this.Api.getUserCredentialInfo().subscribe(credentials => {
      this.updateForm(credentials);
      this.isLoading = false;
      this.showSuccessModal();
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  showSuccessModal() {
    this.successModal.show();
    window.setTimeout(() => this.successModal.hide(), 4000);
  }

  hideSuccessModal() {
    this.successModal.hide();
  }

  updateForm(credentials: PersonCredentialInformation) {
    this.confirmedPassword.setValue('');
    this.credentialInformation.patchValue(credentials, {onlySelf: true});
    this.credentialInformation.markAsPristine();
    this.credentialInformation.markAsUntouched();
  }

  update() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.hideSuccessModal();
    this.isLoading = true;
    const adjustedModel = this.removeUnchangedSectionsFromModel();

    this.Api.updateCredentialInformation(adjustedModel).subscribe(() => {
      this.reloadData();
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  removeUnchangedSectionsFromModel(): PersonCredentialInformation {
    return {
      email: this.emailPart.dirty ? this.emailPart.value : undefined,
      password: (this.passwordPart.dirty && this.oldPassword.value) ? this.passwordPart.value : undefined
    };
  }

  handleError(err: HttpError) {
    this.isLoading = false;

    if (err.status === 400 && err.code === 'incorrect_password') {
      this.isOldPasswordIncorrect = true;
    } else {
      console.error('Failed to save or load credentials', err);
      this.Navigation.handleError(err);
    }
  }

  isUpdateButtonDisabled() {
    return this.credentialInformation.pristine || this.credentialInformation.invalid || this.isOldPasswordIncorrect;
  }
}
